// Small shared pieces of the Learn tab: page head with back link, loading, empty, error and offline states.
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Icon } from './icons';

export function PageHead({ back, backLabel, eyebrow }: { back: string; backLabel: string; eyebrow?: string }) {
  return (
    <div className="learn-head">
      <Link className="back lg" to={back} aria-label={backLabel}>
        <Icon name="back" />
      </Link>
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
    </div>
  );
}

/** A progress ring with a number and a short unit under it ("6 / weeks left"). Decorative: the label says it. */
export function Ring({ value, label, frac }: { value: number; label: string; frac: number }) {
  const r = 40;
  const c = 2 * Math.PI * r;
  return (
    <span className="phase-ring" role="img" aria-label={`${value} ${label}`}>
      <svg viewBox="0 0 108 108" aria-hidden="true" focusable="false">
        <circle className="ring-track" cx="54" cy="54" r={r} />
        {frac > 0 && <circle className="ring-fill" cx="54" cy="54" r={r} strokeDasharray={`${Math.max(0.02, frac) * c} ${c}`} transform="rotate(-90 54 54)" />}
      </svg>
      <span className="ring-text" aria-hidden="true"><b>{value}</b><small>{label}</small></span>
    </span>
  );
}

export function DraftTag({ draft }: { draft?: boolean }) {
  const { t } = useTranslation('learn');
  return draft ? <span className="draft-tag">{t('draft')}</span> : null;
}

/** Quiet placeholder shapes while the first copy loads. */
export function Skeleton({ lines = 3, card = false }: { lines?: number; card?: boolean }) {
  const { t } = useTranslation('learn');
  return (
    <div className={`skeleton${card ? ' is-card' : ''}`} role="status" aria-label={t('state.loading')}>
      {Array.from({ length: lines }, (_, i) => <span key={i} style={{ width: `${92 - ((i * 23) % 40)}%` }} />)}
    </div>
  );
}

/** Full-area state when there is nothing to show: offline, not found or a failure, with a retry. */
export function ErrorState({ code, onRetry, back }: { code: string; onRetry?: () => void; back?: { to: string; label: string } }) {
  const { t } = useTranslation('learn');
  const kind = code === 'offline' ? 'offline' : /not_found$/.test(code) ? 'notFound' : 'generic';
  return (
    <div className="learn-state" role={kind === 'generic' ? 'alert' : 'status'}>
      <Icon name={kind === 'offline' ? 'offline' : kind === 'notFound' ? 'book' : 'retry'} />
      <h2>{t(`state.${kind}.title`)}</h2>
      <p className="muted">{t(`state.${kind}.text`)}</p>
      <div className="learn-state-actions">
        {onRetry && kind !== 'notFound' && <button className="btn btn-quiet" type="button" onClick={onRetry}>{t('state.retry')}</button>}
        {back && <Link className="btn btn-text" to={back.to}>{back.label}</Link>}
      </div>
    </div>
  );
}

/** One muted line above content that came from this phone's copy. */
export function StaleNote({ code }: { code: string | null }) {
  const { t } = useTranslation('learn');
  if (!code) return null;
  return (
    <p className="stale-note" role="status">
      <Icon name={code === 'offline' ? 'offline' : 'retry'} />
      {t(code === 'offline' ? 'state.staleOffline' : 'state.staleError')}
    </p>
  );
}

export function EmptyNote({ title, text }: { title: string; text: string }) {
  return (
    <div className="empty-note">
      <p className="empty-title">{title}</p>
      <p className="muted">{text}</p>
    </div>
  );
}
